import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PersonIcon from "@mui/icons-material/Person";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import { Card, CardContent, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Box } from "@mui/system";
import type { UseMutationResult } from "@tanstack/react-query";
import { type FC, useMemo } from "react";
import { INK, NEUTRAL } from "../../../theme/tokens";
import { AgentStaffActionStatus, type FlatNurseAction } from "../../../utils/models";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";

interface ActionsBoxTileProps {
	shiftDateFormatted: string;
	shiftTime: string;
	neededNurses?: number;
	agentStatus?: AgentStaffActionStatus;
	pauseByRequestMutation: UseMutationResult<void, Error, void>;
	resumeByRequestMutation: UseMutationResult<void, Error, void>;
	isAnyMutationPending: boolean;
	isLoadingMetadata: boolean;
	allActions: FlatNurseAction[];
}

export const ActionsBoxTile: FC<ActionsBoxTileProps> = ({
	shiftDateFormatted,
	shiftTime,
	neededNurses,
	agentStatus,
	pauseByRequestMutation,
	resumeByRequestMutation,
	isAnyMutationPending,
	isLoadingMetadata,
	allActions,
}) => {
	const theme = useTheme();
	const approvedNurseIds = Array.from(new Set(allActions.filter((action) => action.is_approved).map((action) => action.nurse_id)));

	const statusBox = useMemo(() => {
		switch (agentStatus) {
			case AgentStaffActionStatus.Completed:
				return (
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px", alignSelf: "center" }}>
						Done{" "}
						{neededNurses !== undefined && approvedNurseIds.length >= neededNurses
							? " (Successfully)"
							: approvedNurseIds.length > 0
								? " (Partially Successfully)"
								: " (Not Successfully)"}
					</Typography>
				);
			case AgentStaffActionStatus.Paused:
				return (
					<SystemButton
						buttonVariant="primary"
						onClick={() => resumeByRequestMutation.mutate()}
						disabled={isAnyMutationPending || isLoadingMetadata}
						loading={resumeByRequestMutation.isPending}
						leadingIcon={<PlayArrowIcon />}
					>
						Resume
					</SystemButton>
				);
			case AgentStaffActionStatus.Pending:
				return (
					<SystemButton
						buttonVariant="secondary"
						onClick={() => pauseByRequestMutation.mutate()}
						disabled={isAnyMutationPending || isLoadingMetadata}
						loading={pauseByRequestMutation.isPending}
						leadingIcon={<PauseCircleIcon />}
					>
						Pause
					</SystemButton>
				);
			case AgentStaffActionStatus.NotInitiated:
				return (
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px", alignSelf: "center" }}>
						Waiting for agent to start...
					</Typography>
				);
			default:
				return null;
		}
	}, [agentStatus, pauseByRequestMutation, resumeByRequestMutation, isAnyMutationPending, isLoadingMetadata, approvedNurseIds, neededNurses]);

	return (
		<Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center" }}>
			<Card sx={{ flex: 1, border: `1px solid ${NEUTRAL[150]}`, borderRadius: "4px" }}>
				<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
						<CalendarMonthIcon sx={{ fontSize: "14px", color: theme.palette.primary.main }} />
						<Typography variant="caption" sx={{ color: NEUTRAL[500], fontSize: "12px" }}>
							Date
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px" }}>
						{shiftDateFormatted}
					</Typography>
				</CardContent>
			</Card>
			<Card sx={{ flex: 1, border: `1px solid ${NEUTRAL[150]}`, borderRadius: "4px" }}>
				<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
						<WatchLaterIcon sx={{ fontSize: "14px", color: theme.palette.primary.main }} />
						<Typography variant="caption" sx={{ fontSize: "12px", color: alpha(INK.SECONDARY, 0.5) }}>
							Time
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px" }}>
						{shiftTime}
					</Typography>
				</CardContent>
			</Card>
			<Card sx={{ flex: 1, border: `1px solid ${NEUTRAL[150]}`, borderRadius: "4px" }}>
				<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
						<PersonIcon sx={{ fontSize: "14px", color: theme.palette.primary.main }} />
						<Typography variant="caption" sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "12px" }}>
							Needed
						</Typography>
					</Box>
					<Typography variant="body2" sx={{ fontWeight: 600, color: INK.SECONDARY, fontSize: "14px" }}>
						{neededNurses === undefined
							? "—"
							: approvedNurseIds.length
								? `${approvedNurseIds.length} / ${neededNurses} nurse${neededNurses !== 1 ? "s" : ""}`
								: neededNurses}
					</Typography>
				</CardContent>
			</Card>

			<Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>{statusBox}</Box>
		</Box>
	);
};
